import { Box, Button, Checkbox, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Radio, RadioGroup, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminDeleteUsers, adminGetUsers } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminUser, IAdminUserList, ICertificate, ILicense, IPharmacy } from "../../../types";
import CertificateModal from "./Modals/CertificateModal";
import LicenseModal from "./Modals/LicenseModal";
import PharmacyModal from "./Modals/PharmacyModal";
import { StatusDot } from "./Modals/shared";

const USER_COLUMNS = [
    { key: "type", label: "타입" },
    { key: "email", label: "이메일" },
    { key: "name", label: "이름" },
    { key: "phone", label: "전화번호" },
    { key: "isAdAgree", label: "선택약관동의" },
    { key: "status", label: "상태" },
    { key: "certificate", label: "재학증명서" },
    { key: "license", label: "약사정보" },
    { key: "pharmacy", label: "약국정보" },
];

export default function Users() {
    const [page, setPage] = useState(1);
    const size = 100;
    const [status, setStatus] = useState("ALL");
    const [keyword, setKeyword] = useState<string | null>(null);

    interface ISearchForm {
        keyword: string;
    }

    const { register, handleSubmit } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const onSearch = ({ keyword: nextKeyword }: ISearchForm) => {
        setPage(1);
        setKeyword(nextKeyword);
    };

    const { data: userListData, isLoading: isUserListDataLoading } = useQuery<IAdminUserList>({
        queryKey: ["adminGetUsers", page, size, keyword, status],
        queryFn: () => adminGetUsers({ page, size, keyword, status }),
        enabled: true,
    });

    const totalPages = useMemo(() => userListData?.totalPages ?? 0, [userListData]);
    const currentPage = useMemo(() => userListData?.currentPage ?? 0, [userListData]);
    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* 어떤 객체의 모달을 열지, 어떤 유저의 객체를 넘길지 */
    const [selectedUser, setSelectedUser] = useState<IAdminUser | null>(null);
    const [openedModal, setOpenedModal] = useState<"certificate" | "license" | "pharmacy" | null>(null);

    const openObjectModal = (user: IAdminUser, kind: "certificate" | "license" | "pharmacy") => {
        setSelectedUser(user);
        setOpenedModal(kind);
    };

    const closeObjectModal = () => {
        setOpenedModal(null);
        setSelectedUser(null);
    };

    /* selection */

    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const currentPageUserIds = useMemo(() => userListData?.users.map((user) => user.id) ?? [], [userListData]);

    const isAllCurrentPageSelected = useMemo(() => {
        return currentPageUserIds.length > 0 && currentPageUserIds.every((id) => selectedUserIds.includes(id));
    }, [currentPageUserIds, selectedUserIds]);

    const isSomeCurrentPageSelected = useMemo(() => {
        const someSelected = currentPageUserIds.some((id) => selectedUserIds.includes(id));
        return someSelected && !isAllCurrentPageSelected;
    }, [currentPageUserIds, selectedUserIds, isAllCurrentPageSelected]);

    const toggleSelectAllCurrentPage = () => {
        if (isAllCurrentPageSelected) {
            setSelectedUserIds((prev) => prev.filter((id) => !currentPageUserIds.includes(id)));
        } else {
            setSelectedUserIds((prev) => Array.from(new Set([...prev, ...currentPageUserIds])));
        }
    };

    const toggleSelectOne = (userId: string) => {
        setSelectedUserIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
    };

    /* delete users modal */

    const { isOpen: isDeleteUsersModalOpen, onOpen: onOpenDeleteUsersModal, onClose: onCloseDeleteUsersModal } = useDisclosure();

    const onDeleteUsers = () => {
        deleteUsersMutation.mutate({ userIds: selectedUserIds });
        onCloseDeleteUsersModal();
    };

    /* queryClient */

    const queryClient = useQueryClient();

    /* toast */

    const toast = useToast();

    /* mutation */

    const deleteUsersMutation = useMutation({
        mutationFn: adminDeleteUsers,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["adminGetUsers", page, size, keyword, status] });
            setSelectedUserIds([]);
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "유저 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    return (
        <AdminOnly>
            <Stack width="100%" padding={10}>
                <HStack justifyContent="space-between" alignItems="center" gap={10}>
                    <HStack justifyContent="flex-start" alignItems="center" gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>유저</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{userListData?.totalCount}</Text>
                    </HStack>
                </HStack>

                {/* User.status 기준 필터. EDITED / DENIED 는 더 이상 쓰지 않는다. */}
                <RadioGroup
                    value={status}
                    onChange={(next) => {
                        setPage(1);
                        setStatus(next);
                    }}
                >
                    <HStack spacing={6} marginY={4}>
                        <Box paddingX={3} paddingY={1} backgroundColor={colors.light} borderRadius={10}>
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>STATUS</Text>
                        </Box>
                        <Radio value="ALL" colorScheme="blue">ALL</Radio>
                        <Radio value="ACTIVE" colorScheme="blue">ACTIVE</Radio>
                        <Radio value="PENDING" colorScheme="blue">PENDING</Radio>
                        <Radio value="REJECTED" colorScheme="blue">REJECTED</Radio>
                        <Radio value="SUSPENDED" colorScheme="blue">SUSPENDED</Radio>
                    </HStack>
                </RadioGroup>

                <HStack as="form" onSubmit={handleSubmit(onSearch)} marginY={10}>
                    <Input
                        {...register("keyword", { required: false })}
                        type="text"
                        placeholder="Search"
                        width="50%"
                    />
                    <Box as="button" type="submit">
                        <MyIcon src="/svg/search.svg" alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                {/* trash button */}

                <HStack justifyContent={"flex-start"} marginY={0}>
                    <Button
                        size={"sm"}
                        variant={"solid"}
                        isDisabled={selectedUserIds.length === 0}
                        opacity={selectedUserIds.length === 0 ? 0.1 : 1}
                        onClick={onOpenDeleteUsersModal}
                    >
                        <MyIcon src={"/svg/trash-filled.svg"} alt="trash" width={16} height={16} />
                    </Button>
                    <Modal isOpen={isDeleteUsersModalOpen} onClose={onCloseDeleteUsersModal}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Text fontSize={20} fontWeight={700} color={colors.black}>삭제하기</Text>
                                <ModalCloseButton />
                            </ModalHeader>
                            <ModalBody>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>선택된 행 수: {selectedUserIds.length}</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>정말 삭제하시겠습니까?</Text>
                            </ModalBody>
                            <Stack marginTop={5} width={"100%"} padding={3}>
                                <Button
                                    onClick={onDeleteUsers}
                                    isLoading={deleteUsersMutation.isPending}
                                    width={"100%"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    borderRadius={10}
                                    _hover={{ bg: colors.blue }}
                                    boxShadow="sm"
                                >
                                    삭제
                                </Button>
                            </Stack>
                        </ModalContent>
                    </Modal>
                </HStack>

                {!isUserListDataLoading ? (
                    <TableContainer marginTop={5}>
                        <Table variant="simple" scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    <Th>
                                        <Checkbox
                                            isChecked={isAllCurrentPageSelected}
                                            isIndeterminate={isSomeCurrentPageSelected}
                                            onChange={toggleSelectAllCurrentPage}
                                        />
                                    </Th>
                                    {USER_COLUMNS.map((column) => (
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {userListData?.users.map((user) => (
                                    <Tr key={user.id}>
                                        <Td>
                                            <Checkbox
                                                isChecked={selectedUserIds.includes(user.id)}
                                                onChange={() => toggleSelectOne(user.id)}
                                            />
                                        </Td>
                                        <Td>{user.type ?? "-"}</Td>
                                        <Td>{user.email ?? "-"}</Td>
                                        <Td>{user.name ?? "-"}</Td>
                                        <Td>{user.phone ?? "-"}</Td>
                                        <Td>{String(user.isAdAgree)}</Td>
                                        <Td>
                                            <StatusCell status={user.status} />
                                        </Td>
                                        <Td>
                                            <ObjectStatusButton
                                                status={user.certificate?.status}
                                                onClick={() => openObjectModal(user, "certificate")}
                                            />
                                        </Td>
                                        <Td>
                                            <ObjectStatusButton
                                                status={user.license?.status}
                                                onClick={() => openObjectModal(user, "license")}
                                            />
                                        </Td>
                                        <Td>
                                            <ObjectStatusButton
                                                status={user.pharmacy?.status}
                                                onClick={() => openObjectModal(user, "pharmacy")}
                                            />
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                <HStack justifyContent="center" marginTop={5}>
                    {page > 1 && (
                        <Button size="xs" variant="ghost" onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
                    {page > 5 && (
                        <>
                            <Button size="xs" variant="ghost" onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
                    {pages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size="xs"
                            variant="ghost"
                            onClick={() => setPage(pageNumber)}
                            backgroundColor={pageNumber === currentPage ? colors.blue : ""}
                            color={pageNumber === currentPage ? colors.white : ""}
                        >
                            {pageNumber}
                        </Button>
                    ))}
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size="xs" variant="ghost" onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
                    {page < totalPages && (
                        <Button size="xs" variant="ghost" onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>

                {selectedUser?.certificate && (
                    <CertificateModal
                        isOpen={openedModal === "certificate"}
                        onClose={closeObjectModal}
                        userId={selectedUser.id}
                        certificate={selectedUser.certificate as ICertificate}
                    />
                )}
                {selectedUser?.license && (
                    <LicenseModal
                        isOpen={openedModal === "license"}
                        onClose={closeObjectModal}
                        userId={selectedUser.id}
                        license={selectedUser.license as ILicense}
                    />
                )}
                {selectedUser?.pharmacy && (
                    <PharmacyModal
                        isOpen={openedModal === "pharmacy"}
                        onClose={closeObjectModal}
                        userId={selectedUser.id}
                        pharmacy={selectedUser.pharmacy as IPharmacy}
                    />
                )}
            </Stack>
        </AdminOnly>
    );
}

function StatusCell({ status }: { status?: string }) {
    if (!status) return <Text>-</Text>;

    return (
        <HStack gap={2}>
            <StatusDot status={status} />
            <Text fontSize={14}>{status}</Text>
        </HStack>
    );
}

/* 객체가 없으면 '-', 있으면 status 텍스트가 모달 버튼이 된다. */
function ObjectStatusButton({
    status,
    onClick,
}: {
    status?: string;
    onClick: () => void;
}) {
    if (!status) return <Text>-</Text>;

    return (
        <HStack
            as="button"
            gap={2}
            cursor="pointer"
            onClick={onClick}
        >
            <StatusDot status={status} />
            <Text fontSize={14} fontWeight={500} textDecoration="underline">
                {status}
            </Text>
        </HStack>
    );
}
